/**
 * READING A TILL'S LOGCAT — pure, no React, no network.
 *
 * `collect_logs` uploads `logcat.txt` as Android's `threadtime` format:
 *
 *   10-06 11:42:33.912  4321  4410 W Printer : SDK write slow: 2,640 ms
 *   MM-DD HH:MM:SS.mmm  PID   TID  L Tag     : message
 *
 * Any line that does not match (a "--------- beginning of main" banner, a
 * wrapped stack-trace line, a build that logs differently) is kept as a plain
 * line with no level — never dropped, because the line you cannot parse is
 * often the one you needed.
 */

import type { FleetCommand } from '@platform/shared-types';
import type { FleetRow } from '../../pos/fleet/fleetRows';

export type LogLevel = 'V' | 'D' | 'I' | 'W' | 'E' | 'F' | 'A';

export interface LogLine {
  /** 1-based line number in the file, kept through filtering. */
  n: number;
  raw: string;
  /** `10-06 11:42:33.912`; absent on an unparsed line. */
  ts?: string;
  level?: LogLevel;
  tag?: string;
  msg?: string;
}

const THREADTIME = /^(\d\d-\d\d\s+\d\d:\d\d:\d\d\.\d{3})\s+\d+\s+\d+\s+([VDIWEFA])\s+(.*?)\s*:\s?(.*)$/;

export function parseLogcat(text: string): LogLine[] {
  const rows = text.split(/\r?\n/);
  // A trailing newline is not a line.
  if (rows.length && rows[rows.length - 1] === '') rows.pop();
  return rows.map((raw, i) => {
    const m = THREADTIME.exec(raw);
    if (!m) return { n: i + 1, raw };
    return { n: i + 1, raw, ts: m[1].replace(/\s+/, ' '), level: m[2] as LogLevel, tag: m[3], msg: m[4] };
  });
}

/** All / Errors / Warn+ — the mockup's three-way level toggle. */
export type LevelFilter = 'all' | 'errors' | 'warn';

const ERRORISH = new Set<LogLevel>(['E', 'F', 'A']);

export function keepLevel(line: LogLine, filter: LevelFilter): boolean {
  if (filter === 'all') return true;
  if (!line.level) return false;
  if (ERRORISH.has(line.level)) return true;
  return filter === 'warn' && line.level === 'W';
}

export function filterLines(lines: LogLine[], filter: LevelFilter, term: string): LogLine[] {
  const t = term.trim().toLowerCase();
  return lines.filter((l) => keepLevel(l, filter) && (!t || l.raw.toLowerCase().includes(t)));
}

/** Tailwind text colour per level — E red, W amber, I sky, D/V muted. */
export function levelClass(level: LogLevel | undefined): string {
  switch (level) {
    case 'E': case 'F': case 'A': return 'text-red-400';
    case 'W': return 'text-amber-300';
    case 'I': return 'text-sky-300';
    default: return 'text-stone-400';
  }
}

/** `glass-side` from "Glass Side"; never empty. */
function slug(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'till';
}

const pad = (n: number) => String(n).padStart(2, '0');

/**
 * `logcat-glass-side-2026-10-06-1153.txt` — register name, else tracking id,
 * else device id, then the capture's local time. Local, not UTC: the operator
 * matches it against the clock they were looking at when the till misbehaved.
 */
export function captureFileName(row: Pick<FleetRow, 'registerName' | 'trackingId' | 'deviceId'>, at: number, kind: 'logcat' | 'screen', ext: 'txt' | 'png'): string {
  const who = slug(row.registerName?.trim() || row.trackingId || row.deviceId || 'till');
  const d = new Date(at);
  const stamp = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}`;
  return `${kind}-${who}-${stamp}.${ext}`;
}

/** S3 keeps captures 7 days (bucket lifecycle rule); past that the link is gone. */
export const CAPTURE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export const CAPTURE_ACTIONS = new Set(['collect_logs', 'capture_screen']);

export function isCapture(c: FleetCommand): boolean {
  return CAPTURE_ACTIONS.has(c.action);
}

export function isExpiredCapture(c: FleetCommand, now: number = Date.now()): boolean {
  return now - (c.completedAt ?? c.createdAt) > CAPTURE_TTL_MS;
}

/** `412 KB` / `1.9 MB` from `result.bytes`, or null when the till did not say. */
export function captureSize(c: FleetCommand): string | null {
  const b = (c.result as Record<string, unknown> | null)?.bytes;
  if (typeof b !== 'number' || !Number.isFinite(b) || b < 0) return null;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

/** Save text or a blob as a file — Blob + object URL, no server round trip. */
export function saveFile(data: Blob, filename: string): void {
  const url = URL.createObjectURL(data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke on the next tick: some browsers start the download asynchronously.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** Clipboard with a fallback for the iOS webview / insecure origins. */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* fall through to the textarea path */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    return ok;
  } catch {
    return false;
  }
}
